import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  closestCenter,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import debounce from 'lodash.debounce'
import { changeLecturePosition } from 'modules/courses/courses/curriculum/api/lecture-api'
import { changeModulePosition } from 'modules/courses/courses/curriculum/api/module-api'
import CreateLectureModal from 'modules/courses/courses/curriculum/components/create-lecture-modal'
import { ModuleLecture } from 'modules/courses/courses/curriculum/components/module-lecture'
import {
  CourseCurriculumCommonInterface,
  CourseCurriculumInterface,
} from 'modules/courses/courses/curriculum/types/curriculum-interface'
import { ModuleInterface } from 'modules/courses/courses/curriculum/types/module-interface'
import {
  getSelectedСoursesCurriculumFromStorage,
  setSelectedСoursesCurriculumToStorage,
} from 'modules/courses/courses/curriculum/utils/storage-utils'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { KeyedMutator } from 'swr'
import { LightCourseLectureActions } from '../../light-course/components/light-course-lecture-actions'
import { LightCourseModuleActions } from '../../light-course/components/light-course-module-actions'
import { LightCourseCurriculumInterface } from '../../light-course/light-course-interface'
import { CourseId } from '../../types/course-interface'
import { LectureId } from '../types/lecture-interface'
import { nonDraggableProps } from '../utils/non-draggable-props'
import { AddLectureButton, AddLectureButtonProps } from './add-lecture-button'
import { CourseModule } from './course-module'
import LectureActions from './lecture-actions'
import ModuleActions from './module-actions'

type LightCourseProps = {
  isLightCourse: true
  modules: LightCourseCurriculumInterface['modules']
  mutate: KeyedMutator<LightCourseCurriculumInterface>
  onAddLectureClick: (
    moduleId: number,
    setFetching: Parameters<AddLectureButtonProps['onClick']>[0],
  ) => void
  onEditLecture: (lectureId: LectureId) => void
  onLectureNameClick: (lectureId: LectureId) => void
}

type CourseProps = {
  isLightCourse?: false
  modules: ModuleInterface[]
  mutate: KeyedMutator<CourseCurriculumInterface>
  onAddLectureClick?: never
  onEditLecture?: never
  onLectureNameClick?: never
}

export type CourseCurriculumTableProps = {
  courseId: CourseId
} & (LightCourseProps | CourseProps)

function CourseCurriculumTable({
  courseId,
  isLightCourse,
  modules,
  mutate,
  onAddLectureClick,
  onEditLecture,
  onLectureNameClick,
}: CourseCurriculumTableProps) {
  const activeIdRef = useRef<UniqueIdentifier | null>(null)
  const clonedItemsRef = useRef<typeof modules | null>(null)
  const movingToNewModuleRef = useRef(false)
  const [lecturesChangingPosition, setLecturesChangingPosition] = useState<number[]>([])
  const containers = modules.map(module => module.id) as UniqueIdentifier[]
  const checkIfContainer = (element: { id: UniqueIdentifier }) => containers.includes(element.id)

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))
  const selectedСoursesCurriculumCookie = useMemo(
    () => getSelectedСoursesCurriculumFromStorage(courseId),
    [courseId],
  )

  /**
   * @description spread inner content as its type contains only intersection of types
   */
  const sortingMutator = mutate as unknown as KeyedMutator<CourseCurriculumCommonInterface>

  const collisionDetectionStrategy = useCallback(
    debounce<CollisionDetection>(
      args => {
        if (activeIdRef.current && checkIfContainer({ id: activeIdRef.current })) {
          return closestCenter({
            ...args,
            droppableContainers: args.droppableContainers.filter(checkIfContainer),
          })
        }
        return closestCorners(args)
      },
      50,
      {
        leading: true,
        trailing: false,
        maxWait: 50,
      },
    ),
    [],
  )

  const findContainer = (id: UniqueIdentifier) => {
    if (containers.includes(id)) {
      return id
    }
    return modules.find(module => {
      const searchLecture = module.lectures.find(lecture => lecture.id === id)
      if (searchLecture) {
        return module
      }
    })?.id
  }

  const onDragCancel = async () => {
    const clonedItems = clonedItemsRef.current
    if (clonedItems) {
      await sortingMutator(data => {
        if (data) {
          return {
            ...data,
            modules: clonedItems,
          }
        }
      })
    }
    activeIdRef.current = null
    clonedItemsRef.current = null
    movingToNewModuleRef.current = false
  }

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    const overId = over?.id

    if (containers.includes(active.id) && overId) {
      const activeIndex = modules.findIndex(module => module.id === active.id)
      const overIndex = modules.findIndex(module => module.id === over.id)

      if (activeIndex === overIndex) {
        activeIdRef.current = null
        clonedItemsRef.current = null
        movingToNewModuleRef.current = false
        return
      }

      await sortingMutator(data => {
        if (data) {
          return {
            ...data,
            modules: arrayMove(data.modules, activeIndex, overIndex),
          }
        }
      }, false)
      await changeModulePosition(Number(active.id), { new_position: overIndex + 1 })

      activeIdRef.current = null
      clonedItemsRef.current = null
      movingToNewModuleRef.current = false
      return
    }

    const activeContainer = findContainer(active.id)

    if (!activeContainer || overId == null) {
      activeIdRef.current = null
      clonedItemsRef.current = null
      movingToNewModuleRef.current = false
      return
    }

    const overContainer = findContainer(overId)
    if (!overContainer) {
      activeIdRef.current = null
      clonedItemsRef.current = null
      movingToNewModuleRef.current = false
      return
    }

    const moduleOver = modules.find(module => module.id === overContainer)
    const moduleActive = modules.find(module => module.id === activeContainer)

    if (!moduleOver || !moduleActive) {
      activeIdRef.current = null
      clonedItemsRef.current = null
      movingToNewModuleRef.current = false
      return
    }

    const activeIndex = moduleActive.lectures.findIndex(lecture => lecture.id === active.id)
    const overIndex = moduleOver.lectures.findIndex(lecture => lecture.id === overId)

    if (
      activeIndex === overIndex &&
      activeContainer === overContainer &&
      !movingToNewModuleRef.current
    ) {
      activeIdRef.current = null
      clonedItemsRef.current = null
      movingToNewModuleRef.current = false
      return
    }

    if (activeIndex === -1 || overIndex === -1) {
      activeIdRef.current = null
      clonedItemsRef.current = null
      movingToNewModuleRef.current = false
      return
    }

    await sortingMutator(data => {
      if (data) {
        if (moduleOver.id === moduleActive.id) {
          return {
            ...data,
            modules: data.modules.map(module => {
              if (
                activeIndex > module.lectures.length - 1 ||
                overIndex > module.lectures.length - 1
              ) {
                return module
              }
              if (module.id === overContainer) {
                return {
                  ...module,
                  lectures: arrayMove(module.lectures, activeIndex, overIndex),
                }
              }
              return module
            }),
          }
        } else {
          return data
        }
      }
      return data
    }, false)

    try {
      setLecturesChangingPosition(prev => [...prev, Number(active.id)])
      if (movingToNewModuleRef.current) {
        await changeLecturePosition(Number(active.id), {
          new_position: overIndex + 1,
          module: moduleOver.id,
        })
      } else {
        await changeLecturePosition(Number(active.id), {
          new_position: overIndex + 1,
        })
      }
    } catch (error) {
      if (clonedItemsRef.current) {
        await sortingMutator(data => {
          if (data) {
            const originalActiveModule = clonedItemsRef.current?.find(m => m.id === activeContainer)
            const originalOverModule = clonedItemsRef.current?.find(m => m.id === overContainer)

            if (originalActiveModule && originalOverModule) {
              return {
                ...data,
                modules: data.modules.map(module => {
                  if (module.id === activeContainer) {
                    return { ...module, lectures: originalActiveModule.lectures }
                  }
                  if (module.id === overContainer && activeContainer !== overContainer) {
                    return { ...module, lectures: originalOverModule.lectures }
                  }
                  return module
                }),
              }
            }
          }
          return data
        }, false)
      }
    } finally {
      setLecturesChangingPosition(prev => prev.filter(el => el !== Number(active.id)))
    }

    activeIdRef.current = null
    clonedItemsRef.current = null
    movingToNewModuleRef.current = false
  }

  const onDragOver = async ({ active, over }: DragOverEvent) => {
    const overId = over?.id
    if (overId === undefined || overId === null || containers.includes(active.id)) {
      return
    }

    const overContainerId = findContainer(overId)
    const activeContainerId = findContainer(active.id)

    if (!overContainerId || !activeContainerId) {
      return
    }

    if (activeContainerId !== overContainerId) {
      movingToNewModuleRef.current = true
      const activeModule = modules.find(module => module.id === activeContainerId)
      const overModule = modules.find(module => module.id === overContainerId)
      const activeItems = activeModule?.lectures
      const overItems = overModule?.lectures
      const overIndex = overItems?.findIndex(el => el.id === overId)
      const activeIndex = activeItems?.findIndex(el => el.id === active.id)
      let newIndex: number | undefined = undefined

      if (containers.includes(overId) && overItems) {
        newIndex = overItems.length
      } else {
        const isBelowOverItem =
          over &&
          active.rect.current.translated &&
          active.rect.current.translated.top > over.rect.top + over.rect.height

        const modifier = isBelowOverItem ? 1 : 0
        if (overItems) {
          newIndex = overIndex && overIndex >= 0 ? overIndex + modifier : overItems.length
        }
      }

      await sortingMutator(data => {
        if (
          data &&
          typeof newIndex === 'number' &&
          activeModule &&
          typeof activeIndex === 'number'
        ) {
          return {
            ...data,
            modules: data.modules.map(module => {
              if (module.id === activeContainerId) {
                return {
                  ...module,
                  lectures: module.lectures.filter(item => item.id !== active.id),
                }
              }
              if (module.id === overContainerId) {
                return {
                  ...module,
                  lectures: [
                    ...module.lectures.slice(0, newIndex),
                    activeModule.lectures[activeIndex],
                    ...module.lectures.slice(newIndex),
                  ],
                }
              }
              return module
            }),
          }
        }
        return data
      }, false)
    }
  }

  function onSelectCoursesCurriculum(CurriculumId: number) {
    setSelectedСoursesCurriculumToStorage(courseId, CurriculumId)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={({ active }) => {
        activeIdRef.current = active.id
        clonedItemsRef.current = JSON.parse(JSON.stringify(modules))
        movingToNewModuleRef.current = false
      }}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={modules} strategy={verticalListSortingStrategy}>
        <div className="mt-5 flex flex-col gap-5">
          {isLightCourse
            ? modules.map(module => (
                <CourseModule
                  key={module.id}
                  module={module}
                  childrenCount={module.lectures.length}
                  openDefault={module.id === Number(selectedСoursesCurriculumCookie)}
                  moduleActions={<LightCourseModuleActions module={module} mutate={mutate} />}
                  onSelect={onSelectCoursesCurriculum}
                >
                  <SortableContext items={module.lectures} strategy={verticalListSortingStrategy}>
                    {module.lectures.map((lecture, index) => {
                      return (
                        <ModuleLecture
                          key={lecture.id}
                          disabled={lecturesChangingPosition.includes(lecture.id)}
                          isFetching={lecturesChangingPosition.includes(lecture.id)}
                          lecture={lecture}
                          isLastChild={index === module.lectures.length - 1}
                          data-test={`module-${module.id}-lecture-${lecture.id}`}
                          onNameClick={e => {
                            e.preventDefault()
                            onLectureNameClick(lecture.id)
                          }}
                          lectureActions={
                            <LightCourseLectureActions
                              onEdit={() => onEditLecture(lecture.id)}
                              mutate={mutate}
                              lecture={lecture}
                              moduleId={module.id}
                            />
                          }
                        />
                      )
                    })}
                    <div className="align-center flex justify-center py-5">
                      <div {...nonDraggableProps}>
                        <AddLectureButton
                          onClick={setFetching => onAddLectureClick(module.id, setFetching)}
                          data-test={'module-create-lecture'}
                        />
                      </div>
                    </div>
                  </SortableContext>
                </CourseModule>
              ))
            : modules.map(module => (
                <CourseModule
                  key={module.id}
                  module={module}
                  childrenCount={module.lectures.length}
                  openDefault={module.id === Number(selectedСoursesCurriculumCookie)}
                  moduleActions={<ModuleActions module={module} mutate={mutate} />}
                  onSelect={onSelectCoursesCurriculum}
                >
                  <SortableContext items={module.lectures} strategy={verticalListSortingStrategy}>
                    {module.lectures.map((lecture, index) => {
                      return (
                        <ModuleLecture
                          key={lecture.id}
                          disabled={lecturesChangingPosition.includes(lecture.id)}
                          isFetching={lecturesChangingPosition.includes(lecture.id)}
                          lecture={lecture}
                          isLastChild={index === module.lectures.length - 1}
                          data-test={`module-${module.id}-lecture-${lecture.id}`}
                          lectureActions={
                            <LectureActions
                              moduleId={module.id}
                              lecture={lecture}
                              mutate={mutate}
                            />
                          }
                        />
                      )
                    })}
                    <div className="align-center flex justify-center py-5">
                      <div {...nonDraggableProps}>
                        <CreateLectureModal moduleId={module.id} mutate={mutate} />
                      </div>
                    </div>
                  </SortableContext>
                </CourseModule>
              ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default CourseCurriculumTable
